import { v4 as uuid } from 'uuid';
import { readDatabase, saveDatabase, adoptSystemChanges, pageRecords, type MockRecord } from './database.ts';
import { seedDatabase } from './seed.ts';
import { applyBusinessDemoCleanup, seedBusinessDemoDatabase } from './businessDemoSeed.ts';
import { applyLegacyVendorUsers } from './legacyVendorUsers.ts';
import type { MockRequest } from './adapter.ts';
import { useAuthStore } from './session.ts';

export const db = () => {
  const testing = import.meta.env?.MODE === 'test';
  const database = readDatabase(testing ? seedDatabase : seedBusinessDemoDatabase);
  if (!testing) {
    const cleaned = applyBusinessDemoCleanup(database);
    const mapped = applyLegacyVendorUsers(database);
    if (cleaned || mapped) { adoptSystemChanges(); saveDatabase(); }
  }
  return database;
};
export const values = (request: MockRequest) => Object.fromEntries(Object.entries({ ...request.params, ...request.body }).map(([key, value]) => [key.toLowerCase(), value]));
export const camelBody = (request: MockRequest) => Object.fromEntries(Object.entries(request.body).map(([key, value]) => {
  let normalized = key;
  const leadingAcronym = normalized.match(/^[A-Z0-9]+(?=[A-Z][a-z]|$)/)?.[0];
  if (leadingAcronym && leadingAcronym.length > 1) normalized = normalized.replace(leadingAcronym, leadingAcronym.toLowerCase());
  if (normalized.length && normalized[0] === normalized[0].toUpperCase()) normalized = normalized[0].toLowerCase() + normalized.slice(1);
  return [normalized, value];
}));
const aliases: Record<string, string[]> = {
  oid: ['oid', 'oId'], prodid: ['productId', 'prodId', 'oid'], productid: ['productId', 'prodId', 'oid'],
  prodname: ['prodName', 'name'], productname: ['prodName', 'name'],
  vendors: ['vendorId'], vendorprodname: ['vendorProductName'], pslid: ['pslID', 'pslId', 'pslid', 'psliD', 'pSLId'],
  approvedsupplier: ['vendorId'], prodformername: ['formerName'], proddescription: ['description'],
  prodtypeid: ['productTypeId'], shelflife: ['shelfLifeYears'], catmanagerid: ['catManagerId', 'catMgrId'],
};
const controlKeys = new Set(['startindex', 'maximumrows', 'sortby', 'sortexpression', 'page', 'pagesize', 'currentuser', 'curuser', 'user', 'applicationname']);

export function filterRecords(records: MockRecord[], input: Record<string, unknown>): MockRecord[] {
  return records.filter((record) => Object.entries(input).every(([rawKey, value]) => {
    const key = rawKey.toLowerCase();
    if (value === undefined || value === null || value === '' || value === '00000000-0000-0000-0000-000000000000' || controlKeys.has(key)) return true;
    if (key === 'statuses' || key === 'status') return String(value).split(',').some((status) => status === String(record.statusId ?? record.status) || status.toLowerCase() === String(record.statusName).toLowerCase());
    if (key === 'dtfrom' || key === 'dtto') {
      const timestamp = Date.parse(String(record.issueDate ?? record.dateRcvd ?? record.createdDate ?? ''));
      const bound = Date.parse(String(value));
      const end = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? bound + 86400000 - 1 : bound;
      return key === 'dtfrom' ? timestamp >= bound : timestamp <= end;
    }
    const names = aliases[key] ?? Object.keys(record).filter((name) => name.toLowerCase() === key);
    if (!names.length) return true;
    return names.some((name) => {
      const actual = record[name];
      if (actual === undefined) return false;
      if (typeof value === 'boolean') return actual === value;
      if (key.endsWith('id') || key === 'oid') return String(actual).toLowerCase() === String(value).toLowerCase();
      return String(actual).toLowerCase().includes(String(value).replaceAll('%', '').toLowerCase());
    });
  }));
}

type RecordScope = (request: MockRequest, records: MockRecord[]) => MockRecord[];
let recordScope: RecordScope | undefined;

// Registered by the runtime so helpers stay free of identity imports.
export function setRecordScope(scope: RecordScope | undefined): void {
  recordScope = scope;
}

export function scopeRecords(request: MockRequest, records: MockRecord[]): MockRecord[] {
  return recordScope ? recordScope(request, records) : records;
}

export function list(table: string, request: MockRequest, shape: 'array' | 'paged' | 'count' = 'array') {
  const input = values(request);
  const records = scopeRecords(request, filterRecords(db()[table] ?? [], input));
  if (shape === 'count') return records.length;
  const page = pageRecords(records, Number(input.startindex ?? 0), Number(input.maximumrows ?? records.length), String(input.sortby ?? input.sortexpression ?? ''));
  return shape === 'paged' ? page : page.items;
}

export function find(table: string, id: unknown): MockRecord {
  const record = db()[table]?.find((entry) => entry.oid === id || (table === 'users' && entry.userId === id));
  if (!record) throw new Error(`${table}: record not found.`);
  return record;
}

export function insert(table: string, data: Record<string, unknown>): MockRecord {
  const record = { ...data, oid: uuid(), createdDate: new Date().toISOString() };
  (db()[table] ??= []).push(record);
  saveDatabase();
  return record;
}

export function update(table: string, id: unknown, data: Record<string, unknown>): MockRecord {
  const record = find(table, id);
  data = Object.fromEntries(Object.entries(data).map(([key, value]) => [Object.keys(record).find((existing) => existing.toLowerCase() === key.toLowerCase()) ?? key, value]));
  const changes = Object.entries(data).filter(([key, value]) => !/password|answer|token|secret/i.test(key) && value !== undefined && record[key] !== value)
    .map(([field, value]) => ({ field, old: String(record[field] ?? ''), new: String(value ?? ''), oldValue: String(record[field] ?? ''), newValue: String(value ?? '') }));
  Object.assign(record, data, { lastUpdateDate: new Date().toISOString() });
  db().audit.push({ oid: uuid(), auditId: db().audit.length + 1, recordId: id, table, changes,
    updatedBy: db().users?.find((user) => `mock-session:${user.userId}` === useAuthStore.getState().accessToken)?.userName ?? 'Local system', updatedDate: new Date().toISOString(), action: 'U' });
  saveDatabase();
  return record;
}

export function remove(table: string, id: unknown): boolean {
  find(table, id);
  db()[table] = db()[table].filter((record) => record.oid !== id);
  saveDatabase();
  return true;
}